// Typed client for /api/admin/users/* — the admin user-management surface.
// Follows the project pattern: API_BASE + apiFetch (auth + base URL come
// from there). All endpoints are AccountManagerOrAbove-gated server-side.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

/** Row of the admin users list — mirrors the backend AdminUserDto. */
export interface AdminUser {
  id: string;
  email: string;
  roles: string[];
  tier: string;
  tierExpiresAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  isLockedOut: boolean;
  online: boolean;
  connectionCount: number;
  lastSeenAt: string | null;
}

/** One row of a user's login/registration history. */
export interface UserLoginEvent {
  occurredAt: string;
  kind: string;      // "Login" | "Register"
  outcome: string;   // "Success" | "FailedBadCredentials" | "FailedLockedOut"
  ipAddress: string | null;
  userAgent: string | null;
}

/** High-level activity summary for a user. */
export interface UserSummary {
  userId: string;
  strategyCount: number;
  runCount: number;
  scannerCount: number;
  lastActivityAt: string | null;
  online: boolean;
}

/** Aggregated usage for one feature area. */
export interface FeatureUsage {
  feature: string;
  count: number;
  lastActivityAt: string;
}

/** System-wide new-account registration policy. */
export interface RegistrationPolicy {
  registrationEnabled: boolean;
  updatedAt: string | null;
  updatedByUserId: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

async function sendJson<T>(path: string, method: 'POST' | 'PUT', body: unknown): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

const id = (userId: string) => encodeURIComponent(userId);

export const adminUsersService = {
  /** All users, ordered by email. */
  list: () => getJson<AdminUser[]>('/api/admin/users'),

  /** One server-paged, searched, sorted page of users.
   *  There is deliberately no "online only" option here: presence is in-memory and
   *  unknown to the database, so combining it with paging would page over the wrong
   *  set. Use onlineList() for that view — it is naturally small. */
  listPaged: (opts: {
    page?: number; pageSize?: number; search?: string;
    sortBy?: 'email' | 'createdAt' | 'tier' | 'lastLoginAt'; sortDir?: 'asc' | 'desc';
  } = {}) => {
    const qs = new URLSearchParams();
    if (opts.page != null) qs.set('page', String(opts.page));
    if (opts.pageSize != null) qs.set('pageSize', String(opts.pageSize));
    if (opts.search?.trim()) qs.set('search', opts.search.trim());
    if (opts.sortBy) qs.set('sortBy', opts.sortBy);
    if (opts.sortDir) qs.set('sortDir', opts.sortDir);
    return getJson<{ items: AdminUser[]; total: number; page: number; pageSize: number }>(
      `/api/admin/users/page?${qs.toString()}`);
  },

  /** Users with a live connection right now. */
  onlineList: () => getJson<AdminUser[]>('/api/admin/users/online'),

  /** Rolling login/registration history, newest first. */
  logins: (userId: string, limit = 50) =>
    getJson<UserLoginEvent[]>(`/api/admin/users/${id(userId)}/logins?limit=${limit}`),

  /** High-level activity summary. */
  summary: (userId: string) =>
    getJson<UserSummary>(`/api/admin/users/${id(userId)}/summary`),

  /** Per-feature usage over the last `days` days. */
  usage: (userId: string, days = 30) =>
    getJson<FeatureUsage[]>(`/api/admin/users/${id(userId)}/usage?days=${days}`),

  /** Lock or unlock a user account (the "disable account" control). */
  setLockout: (userId: string, locked: boolean) =>
    sendJson<AdminUser>(`/api/admin/users/${id(userId)}/lockout`, 'POST', { locked }),

  /** Delete a user account. cascade=true also deletes everything the user
   *  owns (strategies, runs, scanners, …) — irreversible. */
  deleteUser: async (userId: string, cascade: boolean): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/api/admin/users/${id(userId)}?cascade=${cascade}`,
      { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  },

  /** Read the system-wide registration policy. SuperAdmin only. */
  getRegistrationPolicy: () =>
    getJson<RegistrationPolicy>('/api/admin/registration'),

  /** Enable or disable new-account registration. SuperAdmin only. */
  setRegistrationEnabled: (enabled: boolean) =>
    sendJson<RegistrationPolicy>('/api/admin/registration', 'PUT', { enabled }),
};
